// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/davService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type DavListaResumo } from './listaFiltros';
import { paramsConsulta, type ComVendedor, type DavKpi } from '../consultas';
import type {
  DavResumo,
  DavDetalhe,
  CriarDavRequest,
  AtualizarDavRequest,
  EstornarDavRequest,
} from '../types';

/** GET /api/dav/cliente/{id}/credito. `disponivel` null = cliente sem limite cadastrado. */
export interface ClienteCredito {
  limite: number;
  emAberto: number;
  vencido: number;
  disponivel: number | null;
}

/** Filtros da lista de DAV/OS/Orçamento: os da lista (busca/status/período/ordem) + cliente e vendedor. */
export interface ListaFiltrosPessoa extends ListaFiltros {
  clienteId?: number;
  vendedorId?: number;
}

/** Query params da lista + cliente/vendedor (0/vazio não vai). */
export const paramsDaListaPessoa = (pagina: number, tamanhoPagina: number, f: ListaFiltrosPessoa = {}) => ({
  ...paramsDaLista(pagina, tamanhoPagina, f),
  clienteId:  f.clienteId  && f.clienteId  > 0 ? f.clienteId  : undefined,
  vendedorId: f.vendedorId && f.vendedorId > 0 ? f.vendedorId : undefined,
});

const BASE = '/api/dav';

export const davService = {
  /** Lista + `resumo` (KPIs de busca+período+cliente+vendedor, sem o status); cada linha traz `vendedorNome`. */
  async getAll(
    pagina = 1, tamanhoPagina = 50, filtros: ListaFiltrosPessoa = {},
  ): Promise<PaginadoComResumo<ComVendedor<DavResumo>, DavListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<ComVendedor<DavResumo>, DavListaResumo>>(BASE, {
      params: paramsDaListaPessoa(pagina, tamanhoPagina, filtros),
    });
    return data;
  },

  /** Limite de compra e saldo em aberto do cliente (frm_dav "R$ Limite Compra"). */
  async getCreditoCliente(clienteId: number): Promise<ClienteCredito> {
    const { data } = await apiClient.get<ClienteCredito>(`${BASE}/cliente/${clienteId}/credito`);
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros: ListaFiltrosPessoa = {}): Promise<DavKpi> {
    const { busca, dtInicio, dtFim, clienteId, vendedorId } = filtros;
    const { data } = await apiClient.get<DavKpi>(`${BASE}/resumo`, {
      params: paramsConsulta({ busca: busca?.trim(), dtInicio, dtFim, clienteId, vendedorId }),
    });
    return data;
  },

  async getAbertos(): Promise<DavResumo[]> {
    const { data } = await apiClient.get<DavResumo[]>(`${BASE}/abertos`);
    return data;
  },

  async getPendentesEntrega(): Promise<DavResumo[]> {
    const { data } = await apiClient.get<DavResumo[]>(`${BASE}/pendentes-entrega`);
    return data;
  },

  async getPorPeriodo(inicio: string, fim: string): Promise<DavResumo[]> {
    const { data } = await apiClient.get<DavResumo[]>(`${BASE}/periodo`, {
      params: { inicio, fim },
    });
    return data;
  },

  async getById(id: number): Promise<DavDetalhe> {
    const { data } = await apiClient.get<DavDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarDavRequest): Promise<DavDetalhe> {
    const { data } = await apiClient.post<DavDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarDavRequest): Promise<DavResumo> {
    const { data } = await apiClient.put<DavResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async copiar(id: number): Promise<DavDetalhe> {
    const { data } = await apiClient.post<DavDetalhe>(`${BASE}/${id}/copiar`);
    return data;
  },

  async estornar(id: number, request: EstornarDavRequest): Promise<DavResumo> {
    const { data } = await apiClient.post<DavResumo>(`${BASE}/${id}/estornar`, request);
    return data;
  },
};
